import { lazy, Suspense, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, useLocation, useNavigate, useRoutes } from 'react-router-dom';
import { keycloakService } from '../../core_web/keycloak';
import LoadingScreen from '../../core_web/minimals.cc/components/LoadingScreen';
import SvgIconStyle from '../../core_web/minimals.cc/components/SvgIconStyle';
import DashboardLayout from '../../core_web/minimals.cc/layouts/dashboard';
import LogoOnlyLayout from '../../core_web/minimals.cc/layouts/LogoOnlyLayout';
import Searchbar from '../components/Searchbar';
import { GetEnrollmentSelected } from '../helpers/session';
import { RootState } from '../store';
import { obterPermissoes } from '../store/logistica.store';
import { PATH_AGENDAMENTOS_APP, PATH_APP } from './paths';

const Loadable = (Component: any) =>
  function (props: any) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { pathname } = useLocation();
    const isDashboard = pathname.includes('/dashboard');

    return (
      <Suspense
        fallback={
          <LoadingScreen
            sx={{
              ...(!isDashboard && {
                top: 0,
                left: 0,
                width: 1,
                zIndex: 9999,
                position: 'fixed'
              })
            }}
          />
        }
      >
        <Component {...props} />
      </Suspense>
    );
  };

export const getIcon = (name: string) => (
  <SvgIconStyle
    src={`${process.env.PUBLIC_URL}/static/icons/navbar/${name}.svg`}
    sx={{ width: '100%', height: '100%' }}
  />
);

export default function Router() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const statePermissoes = useSelector((root: RootState) => root.logistica);
  const [rolesTransportadora, setRolesTransportadora] = useState<string[]>([]);
  const rolesKeycloak = keycloakService.getRoles('logisticaweb');
  const roles: string[] = [...rolesTransportadora, ...rolesKeycloak];

  useEffect(() => {
    dispatch(obterPermissoes());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const currentEnrollment = GetEnrollmentSelected<string>();
    if (Array.isArray(statePermissoes.permissoes) && currentEnrollment) {
      let permissions: string[] = [];
      let x = statePermissoes.permissoes.filter(
        (x) => x.matricula?.toString() === currentEnrollment
      );
      x.forEach((element, index) => {
        element!
          .permissoes!.filter((x) => x.value)
          .forEach((value) => {
            permissions.push(value.key as string);
          });
      });
      setRolesTransportadora(permissions);
    }
  }, [statePermissoes]);

  const onClickSubMenuAgendamentoTransportadora = () => {
    dispatch(obterPermissoes());
    navigate(PATH_AGENDAMENTOS_APP.selecionarMatricula);
  };

  const sidebarConfig = [
    {
      subheader: 'Menu',
      items: [
        {
          title: 'Dashboard',
          path: PATH_APP.root_without_bar,
          icon: getIcon('ic_dashboard')
        },
        {
          onClick: () => onClickSubMenuAgendamentoTransportadora(),
          title: 'Agendamento Transportadora',
          path: PATH_APP.agendamentoTransportadora,
          icon: getIcon('ic_booking'),
          children: [
            {
              title: 'Minhas matriculas',
              path: PATH_AGENDAMENTOS_APP.selecionarMatricula
            },
            {
              title: 'Agendamentos',
              path: PATH_AGENDAMENTOS_APP.agendamento,
              role: 'Agendamentos'
            },
            {
              title: 'Contratos',
              path: PATH_AGENDAMENTOS_APP.contratos,
              role: 'Agendamentos'
            },
            {
              title: 'Solicitações de Pagamento',
              path: PATH_AGENDAMENTOS_APP.solicitacaoPagamento,
              role: 'Pagamentos'
            },
            { title: 'Relatórios', path: PATH_AGENDAMENTOS_APP.relatorio, role: 'Agendamentos' },
            {
              title: 'Cadastro de Veiculo',
              path: PATH_AGENDAMENTOS_APP.cadastroVeiculo,
              role: 'Agendamentos'
            }
          ]
        }
      ]
    }
  ];

  return useRoutes([
    {
      path: PATH_APP.root_without_bar,
      element: (
        <DashboardLayout sidebarConfig={sidebarConfig} roles={roles}>
          <Searchbar />
        </DashboardLayout>
      ),
      children: [
        { path: PATH_APP.root_without_bar, element: <Dashboard /> },
        { path: PATH_AGENDAMENTOS_APP.selecionarMatricula, element: <SelectEnrollmentScreen /> },
        { path: PATH_AGENDAMENTOS_APP.contratos, element: <Contratos /> },
        { path: PATH_AGENDAMENTOS_APP.agendamento, element: <Agendamento /> },
        { path: PATH_AGENDAMENTOS_APP.relatorio, element: <Relatorio /> },
        { path: PATH_AGENDAMENTOS_APP.cadastroVeiculo, element: <CadastroVeiculo /> },
        { path: PATH_AGENDAMENTOS_APP.solicitacaoPagamento, element: <SolicitacaoPagamento /> },
        { path: PATH_AGENDAMENTOS_APP.adicionarAgendamento, element: <AddAgendamento /> },
        {
          path: PATH_AGENDAMENTOS_APP.historicoPagamento,
          element: <SolicitacaoHistoricoPagamento />
        }
      ]
    },
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: '404', element: <NotFound /> },
        { path: '*', element: <Navigate to="/404" replace /> }
      ]
    },
    { path: '*', element: <Navigate to="/404" replace /> }
  ]);
}

// IMPORT COMPONENTS
const Dashboard = Loadable(lazy(() => import('../pages/Dashboard')));
const SelectEnrollmentScreen = Loadable(
  lazy(() => import('../pages/logistica/SelectEnrollmentScreen'))
);
const Contratos = Loadable(lazy(() => import('../pages/logistica/contratos')));
const Agendamento = Loadable(lazy(() => import('../pages/logistica/agendamento')));
const Relatorio = Loadable(lazy(() => import('../pages/logistica/relatorios')));
const CadastroVeiculo = Loadable(lazy(() => import('../pages/logistica/cadastroVeiculo')));
const SolicitacaoPagamento = Loadable(
  lazy(() => import('../pages/logistica/solicitacaoPagamento'))
);
const AddAgendamento = Loadable(lazy(() => import('../pages/logistica/carregamento')));
const SolicitacaoHistoricoPagamento = Loadable(
  lazy(() => import('../pages/logistica/solicitacaoPagamento/historicoPagamento'))
);
const NotFound = Loadable(lazy(() => import('../pages/Page404')));
